import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { styleMap } from "lit/directives/style-map.js";
import { PresetController } from "../helper/controllers/preset-controller";
import {
  arrayPropHasChanged,
  coerceArray,
  EMPTY_ARRAY,
} from "../helper/utils/stable-array";

export type SkySimpleTableCellValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Date;

export type SkySimpleTableRow = Record<string, SkySimpleTableCellValue>;

export type SkySimpleTableColumn = {
  key: string;
  label?: string;
  header?: string;
  align?: "left" | "center" | "right";
  width?: string;
};

/**
 * @element sky-simple-table
 *
 * @summary Lightweight table for rendering simple object rows from `data` and `columns` props.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/simple-table
 *
 * @csspart wrapper - Scrollable table wrapper.
 * @csspart table - The native table element.
 * @csspart header-cell - Header cells.
 * @csspart row - Body rows.
 * @csspart cell - Body cells.
 * @csspart empty - Empty state message.
 *
 * @property {SkySimpleTableRow[]} data - Plain object rows.
 * @property {SkySimpleTableColumn[]} columns - Column definitions. Uses first row keys when omitted.
 * @property {SkySimpleTableColumn[]} column - Alias for `columns`.
 * @property {string} emptyText - Message shown when there are no rows.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 *
 * @example
 * ```html
 * <sky-simple-table></sky-simple-table>
 * <script>
 *   const table = document.querySelector("sky-simple-table");
 *   table.columns = [
 *     { key: "name", label: "Name" },
 *     { key: "role", label: "Role" },
 *     { key: "status", label: "Status" },
 *   ];
 *   table.data = [
 *     { name: "Aarav Patel", role: "Designer", status: "Active" },
 *     { name: "Maya Shah", role: "Engineer", status: "Away" },
 *   ];
 * </script>
 * ```
 */
@customElement("sky-simple-table")
export class SkySimpleTable extends LitElement {
  private _data: SkySimpleTableRow[] = EMPTY_ARRAY as SkySimpleTableRow[];
  @property({ type: Array, hasChanged: arrayPropHasChanged })
  get data(): SkySimpleTableRow[] {
    return this._data;
  }
  set data(value: SkySimpleTableRow[] | null | undefined) {
    this._data = coerceArray(value);
  }

  private _columns: SkySimpleTableColumn[] = EMPTY_ARRAY as SkySimpleTableColumn[];
  @property({ type: Array, hasChanged: arrayPropHasChanged })
  get columns(): SkySimpleTableColumn[] {
    return this._columns;
  }
  set columns(value: SkySimpleTableColumn[] | null | undefined) {
    this._columns = coerceArray(value);
  }

  @property({ type: String }) emptyText = "No data available";

  @property({ type: Array, hasChanged: arrayPropHasChanged })
  get column(): SkySimpleTableColumn[] {
    return this.columns;
  }

  set column(value: SkySimpleTableColumn[] | null | undefined) {
    this.columns = coerceArray(value);
  }


  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static styles = css`
    :host {
      display: block;
      width: 100%;
      color: var(--sky-text-primary);
      font-family: var(--sky-font);
    }

    .wrapper {
      width: 100%;
      overflow-x: auto;
      border: var(--sky-border-primary);
      border-radius: var(--sky-radius-secondary);
      background: var(--sky-glass-primary);
      box-shadow: var(--sky-box-shadow-primary);
    }

    table {
      width: 100%;
      min-width: max-content;
      border-collapse: collapse;
      font-size: var(--sky-font-size-sm, 0.875rem);
    }

    thead {
      background: var(--sky-glass-secondary);
    }

    th,
    td {
      box-sizing: border-box;
      padding: var(--sky-space-3, 12px) var(--sky-space-4, 16px);
      border-bottom: 1px solid var(--sky-border-light);
      text-align: left;
      white-space: nowrap;
    }

    th {
      color: var(--sky-text-secondary);
      font-weight: 700;
      letter-spacing: 0.02em;
    }

    td {
      color: var(--sky-text-primary);
    }

    tbody tr:last-child td {
      border-bottom: 0;
    }

    tbody tr {
      transition: background var(--sky-duration-fast, 120ms)
        var(--sky-ease-default, ease);
    }

    tbody tr:hover {
      background: var(--sky-hover-primary);
    }

    .empty {
      padding: var(--sky-space-6, 24px);
      color: var(--sky-text-secondary);
      text-align: center;
      font-size: var(--sky-font-size-sm, 0.875rem);
    }
  `;

  private get _safeData(): SkySimpleTableRow[] {
    return Array.isArray(this.data) ? this.data : [];
  }

  private get _resolvedColumns(): SkySimpleTableColumn[] {
    if (Array.isArray(this.columns) && this.columns.length > 0) {
      return this.columns.filter((column) => Boolean(column?.key));
    }

    const firstRow = this._safeData[0];
    if (!firstRow) return [];

    return Object.keys(firstRow).map((key) => ({
      key,
      label: this._toTitle(key),
    }));
  }

  private _toTitle(value: string): string {
    return value
      .replace(/[_-]+/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private _cellStyleCache = new WeakMap<
    SkySimpleTableColumn,
    { textAlign?: string; width?: string }
  >();

  private _formatCell(value: SkySimpleTableCellValue): string {
    if (value === null || value === undefined) return "";
    if (value instanceof Date) return value.toLocaleDateString();
    return String(value);
  }

  private _cellStyle(column: SkySimpleTableColumn) {
    const cached = this._cellStyleCache.get(column);
    if (
      cached &&
      cached.textAlign === column.align &&
      cached.width === column.width
    ) {
      return cached;
    }
    const style = {
      textAlign: column.align,
      width: column.width,
    };
    this._cellStyleCache.set(column, style);
    return style;
  }

  render() {
    const rows = this._safeData;
    const columns = this._resolvedColumns;

    if (!rows.length || !columns.length) {
      return html`
        <div class="wrapper" part="wrapper">
          <div class="empty" part="empty">${this.emptyText}</div>
        </div>
      `;
    }

    return html`
      <div class="wrapper" part="wrapper">
        <table part="table">
          <thead>
            <tr>
              ${repeat(
                columns,
                (column) => column.key,
                (column) => html`
                  <th
                    part="header-cell"
                    style=${styleMap(this._cellStyle(column))}
                  >
                    ${column.header ?? column.label ?? this._toTitle(column.key)}
                  </th>
                `,
              )}
            </tr>
          </thead>
          <tbody>
            ${repeat(
              rows,
              (row, index) => String(row.id ?? index),
              (row) => html`
                <tr part="row">
                  ${repeat(
                    columns,
                    (column) => column.key,
                    (column) => html`
                      <td part="cell" style=${styleMap(this._cellStyle(column))}>
                        ${this._formatCell(row[column.key]) || nothing}
                      </td>
                    `,
                  )}
                </tr>
              `,
            )}
          </tbody>
        </table>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sky-simple-table": SkySimpleTable;
  }
}
